import { Component, type ErrorInfo, type ReactNode } from 'react';

// App-level error boundary. Without one, any error thrown during render unmounts
// the ENTIRE React tree and leaves a blank white page (this is what caused the
// Offers Report drilldowns to blank the whole app on a malformed payload). This
// contains the failure to the wrapped subtree and shows a recoverable fallback.
interface Props {
    children: ReactNode;
    // Optional label used in the fallback copy (e.g. "Offers Report").
    label?: string;
    // Optional custom fallback; receives a reset callback to retry the subtree.
    fallback?: (reset: () => void) => ReactNode;
}

interface State {
    hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Keep a console trail for debugging; swap for a logger if one exists.
        console.error('[ErrorBoundary] render error', this.props.label ?? '', error, info.componentStack);
    }

    reset = () => this.setState({ hasError: false });

    render() {
        if (!this.state.hasError) return this.props.children;
        if (this.props.fallback) return this.props.fallback(this.reset);

        const { label } = this.props;
        return (
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, textAlign: 'center', fontFamily: "'DM Sans', -apple-system, sans-serif", color: '#1A1D2E' }}>
                <span className="material-symbols-rounded" style={{ fontSize: 36, color: '#D23A3B', marginBottom: 8 }}>error_outline</span>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Something went wrong</div>
                <div style={{ fontSize: 13, color: '#9CA3B8', marginBottom: 16, maxWidth: 320 }}>
                    {label ? `The ${label} couldn't be displayed.` : "This section couldn't be displayed."} You can try again.
                </div>
                <button
                    type="button"
                    onClick={this.reset}
                    style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#D23A3B', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                    Try again
                </button>
            </div>
        );
    }
}
